import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconTrash, IconView } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface VacancyRow {
  id: number;
  title: string;
  org_unit_id: number;
  org_unit_name: string | null;
  position_id: number | null;
  position_title: string | null;
  status: 'open' | 'closed';
  closing_date: string | null;
  description: string | null;
  candidate_count: number;
}

interface OrgUnitOption {
  id: number;
  unit_name: string;
}

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

const STATUS_BADGE: Record<VacancyRow['status'], string> = {
  open: 'badge-active',
  closed: 'badge-cancelled',
};

interface Filters {
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { dateFrom: '', dateTo: '' };

const emptyForm = { title: '', org_unit_id: '', position_id: '', closing_date: '', description: '' };

export function Vacancies() {
  const { can } = useAuth();
  const confirmAction = useConfirm();
  const { showError } = useToast();
  const navigate = useNavigate();

  const [orgUnits, setOrgUnits] = useState<OrgUnitOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);

  useEffect(() => {
    api.get<OrgUnitOption[]>('/org-units').then((res) => setOrgUnits(res.data));
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, []);

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canCreate = can('hr.create');
  const canArchive = can('hr.archive');

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const query = useMemo(
    () => ({ date_from: filters.dateFrom || undefined, date_to: filters.dateTo || undefined }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<VacancyRow>('/vacancies', query);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<{ id: number }>('/vacancies', {
        ...form,
        position_id: form.position_id || undefined,
        closing_date: form.closing_date || undefined,
      });
      setAdding(false);
      navigate(`/hr/recruitment/${res.data.id}`);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (vacancy: VacancyRow) => {
    if (!(await confirmAction(`Delete the "${vacancy.title}" vacancy and all its candidates?`))) return;
    try {
      await api.delete(`/vacancies/${vacancy.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Recruitment</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <label className="checkbox-inline">
          Closing from
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
        </label>
        <label className="checkbox-inline">
          Closing to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Org Unit</th>
                <th>Position</th>
                <th>Closing Date</th>
                <th>Status</th>
                <th>Candidates</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((vacancy) => (
                  <tr key={vacancy.id}>
                    <td>
                      <strong>{vacancy.title}</strong>
                    </td>
                    <td>{vacancy.org_unit_name ?? '—'}</td>
                    <td className="muted">{vacancy.position_title ?? '—'}</td>
                    <td className="muted">{vacancy.closing_date ?? '—'}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[vacancy.status]}`}>{vacancy.status}</span>
                    </td>
                    <td>{vacancy.candidate_count}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/hr/recruitment/${vacancy.id}`} />
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(vacancy)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No vacancies yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="New vacancy" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Title *
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
              {errors.title && <small className="error">{errors.title[0]}</small>}
            </label>
            <label>
              Org Unit *
              <select value={form.org_unit_id} onChange={(e) => setForm({ ...form, org_unit_id: e.target.value })} required>
                <option value="">Select…</option>
                {orgUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.unit_name}
                  </option>
                ))}
              </select>
              {errors.org_unit_id && <small className="error">{errors.org_unit_id[0]}</small>}
            </label>
            <label>
              Position
              <select value={form.position_id} onChange={(e) => setForm({ ...form, position_id: e.target.value })}>
                <option value="">None</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label className="span-2">
              Closing Date
              <input
                type="date"
                value={form.closing_date}
                onChange={(e) => setForm({ ...form, closing_date: e.target.value })}
              />
              {errors.closing_date && <small className="error">{errors.closing_date[0]}</small>}
            </label>
            <label className="span-2">
              Description
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.title || !form.org_unit_id}>
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
